import { t, Trans } from '@lingui/macro';
import PropTypes from 'prop-types';
import React from 'react';

import createAssets from '@api/v4/assets';
import { getUploadErrorMessageFromBody } from '@components/common/filestack_uploader/helpers';
import { UppyFeatureFlagWrapper } from '@components/common/uppy/UppyFeatureFlagWrapper';
import { mergeAssetOptions } from '@helpers/sweet_alert_options';
import {
  formattedUppyAssets,
  formattedUppyAssetsGrouped,
  uniqueFilesByKey,
  duplicateSweetAlertOptions,
  getUniqueKey
} from '@helpers/uppy';

class AssetUppy extends React.Component {
  triggerClickHandlerRef = React.createRef();

  triggerUpload = (files) => {
    const { activeLabelKey } = this.props;

    const basenames = (files || []).map((f) => f.original_basename);
    const hasSameNameFiles = basenames.length !== new Set(basenames).size;

    if (hasSameNameFiles) {
      window.swal(mergeAssetOptions(), (mergeSelected) => {
        if (mergeSelected) {
          const grouped = {};
          files.forEach((file) => {
            const key = file.original_basename || file.original_name || '';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(file);
          });
          this.uploadAssets(formattedUppyAssetsGrouped(grouped, activeLabelKey));
        } else {
          this.uploadAssets(formattedUppyAssets(files, activeLabelKey));
        }
      });
      return;
    }

    const uniqueKey = getUniqueKey();
    const uniqFiles = uniqueFilesByKey(files, uniqueKey);
    if (files.length !== uniqFiles.length) {
      window.swal(duplicateSweetAlertOptions, (removeDuplicates) => {
        if (removeDuplicates) {
          this.uploadAssets(formattedUppyAssets(uniqFiles, activeLabelKey));
        } else {
          this.uploadAssets(formattedUppyAssets(files, activeLabelKey));
        }
      });
    } else {
      this.uploadAssets(formattedUppyAssets(files, activeLabelKey));
    }
  }

  uploadAssets = (assets) => {
    const { onAssetProcessing, sectionKey } = this.props;
    const options = {
      url: this.uploadEndpoint(),
      section_key: sectionKey,
      data: { attributes: assets }
    };

    onAssetProcessing(assets.length);
    createAssets(options)
      .then((response) => {
        if (response?.data) {
          this.props.newResources(response);
        }
      })
      .catch(async (error) => {
        let message = t`There was an error uploading your file. Please try again.`;

        try {
          const body = error?.json ? await error.json() : error;
          message = getUploadErrorMessageFromBody(body);
        } catch (parseError) {
          // Keep the generic fallback when the response body cannot be parsed.
        }

        Notify.create({
          title: message,
          type: 'error',
        });
      })
      .finally(() => {
        onAssetProcessing(0);
      });
  }

  trigger = () => `uppy-asset-upload-trigger-${this.props.sectionKey}`;

  handleTriggerClick = (event) => {
    if (this.triggerClickHandlerRef.current) {
      this.triggerClickHandlerRef.current(event);
    }
  }

  uploadEndpoint() {
    const brandfolderKey = BF.fx.brandfolder().key;
    const collectionKey = BF.fx.collection() ? BF.fx.collection().key : null;
    return collectionKey ? `/api/v4/collections/${collectionKey}/assets?fast_jsonapi=true&queue_priority=high`
      : `/api/v4/brandfolders/${brandfolderKey}/assets?fast_jsonapi=true&queue_priority=high`;
  }

  renderArea() {
    // Add new uploader class only if old uploader ff is disabled and new one is enabled
    const canAddNewUploaderClass = !BFG.hasFeature('uppy_uploader_features') && BFG.hasFeature('uppy_with_companion_features');
    return (
      <div
        className={`uppy-asset-area ${canAddNewUploaderClass ? 'uppy-asset-area-with-companion' : ''}`}
        id={this.trigger()}
        onClick={this.handleTriggerClick}
      >
        <span className="bff-plus" />
        <p><Trans>Click to add an asset</Trans></p>
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderArea()}
        <UppyFeatureFlagWrapper
          {...this.props}
          button
          handleUpload={this.triggerUpload}
          template="file_ingest"
          trigger={`#${this.trigger()}`}
          onTriggerClickHandler={this.triggerClickHandlerRef}
          uniqIdentifier={`asset-uppy-${this.props.sectionKey}`}
        />
      </React.Fragment>
    );
  }
}

AssetUppy.propTypes = {
  activeLabelKey: PropTypes.string,
  sectionKey: PropTypes.string.isRequired,
  newResources: PropTypes.func.isRequired,
  onAssetProcessing: PropTypes.func,
};

AssetUppy.defaultProps = {
  activeLabelKey: null,
  onAssetProcessing: () => {},
};

export default AssetUppy;
